import { useState, useEffect, useMemo } from "react";

const DEFAULT_INTERVAL = 10000;

/**
 * Time-rotates the tenant's fallback images while the shown cover is a
 * stand-in rather than real album art (FB4-6).
 *
 * A cover counts as a fallback when it's empty, equals the station logo, or is
 * one of the configured pool images (`config.FALLBACK_IMAGES.items`). Real
 * album art is returned unchanged and no timer runs. The cycle starts at the
 * pool member the API picked (not index 0) so the image doesn't visibly snap
 * on the first tick. Known limit: the shared S3-rotation URLs (tenants with no
 * pool AND no logo) aren't recognized and won't rotate client-side.
 *
 * @param cover          current metadata.cover from the API
 * @param fallbackImages config.FALLBACK_IMAGES ({ interval, items } or null)
 * @param logoUrl        config.LOGO_URL
 * @returns the cover URL to display
 */
const useRotatingFallbackCover = (cover, fallbackImages, logoUrl) => {
  const pool = useMemo(
    () =>
      Array.isArray(fallbackImages?.items)
        ? fallbackImages.items.map((i) => i && i.src).filter(Boolean)
        : [],
    [fallbackImages],
  );
  const rotateMs =
    typeof fallbackImages?.interval === "number" && fallbackImages.interval > 0
      ? fallbackImages.interval
      : DEFAULT_INTERVAL;

  const poolIndexOfCover = pool.indexOf(cover);
  const isFallback = !cover || poolIndexOfCover !== -1 || cover === logoUrl;
  const rotating = isFallback && pool.length > 1;

  const [index, setIndex] = useState(0);

  // When the API hands us a pool member, align the cycle to it.
  useEffect(() => {
    if (poolIndexOfCover !== -1) setIndex(poolIndexOfCover);
  }, [poolIndexOfCover]);

  useEffect(() => {
    if (!rotating) return undefined;
    const id = setInterval(() => {
      setIndex((prev) => (prev + 1) % pool.length);
    }, rotateMs);
    return () => clearInterval(id);
  }, [rotating, pool.length, rotateMs]);

  if (!isFallback || pool.length === 0) return cover;
  return pool[index % pool.length];
};

export default useRotatingFallbackCover;
